import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, importProvidersFrom, NgModule } from '@angular/core';
import { GlobalErrorHandler } from './services/global-error-handler';
import { ReactiveFormsModule } from '@angular/forms';
import environment from '../environments/environment';
import { AppRoutingModule } from './app.routing'; // Custom Route Paths
import { AppComponent } from './app.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { SlimLoaderComponent } from './components/shared/slim-loader/slim-loader.component';
import { GableDrawerHostComponent } from './components/gable/gable-drawer-host/gable-drawer-host.component';
import { ReportBugDrawerHostComponent } from './components/bug-report/report-bug-drawer-host/report-bug-drawer-host.component';
import { BreadcrumbBufferService } from './services/bug-report/breadcrumb-buffer.service';
import { LoginGuard } from './common/auth/login.guard';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { DatedBrowserComponent } from './components/shared/dated-browser/dated-browser.component';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import { DragulaModule } from 'ng2-dragula';
import { FileUploadModule } from 'ng2-file-upload';
import { ForwardToFormsGuard } from './common/auth/forward-to-forms.guard';
import { SiteIdInterceptor } from './components/shared/http/site-id.interceptor';
import { ContentTypeInterceptor } from './components/shared/http/content-type.interceptor';
import { RefreshTokenInterceptor } from './components/shared/http/refresh-token.interceptor';
import { ApplicationHttpClient, applicationHttpClientFactory } from './components/shared/http/application-http-client';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { PermissionsGuard } from './common/auth/permissions.guard';
import { MaintenanceModeInterceptor } from './components/shared/http/maintenance-mode.interceptor';
import { CaptureHttpInterceptor } from './components/shared/http/capture-http.interceptor';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { NgxStripeModule, StripeService } from 'ngx-stripe';
import { IncidentBannerService } from './services/incident-banner.service';
import { RuntimeConfigService, AppConfigResponse } from './services/runtime-config.service';
import { TrackingService } from './services/tracking.service';
import { GalleryModule } from 'ng-gallery';
import { RedirectGuard } from './common/auth/redirect.guard';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatLegacyDialogModule } from '@angular/material/legacy-dialog';
import { RichTextEditorComponent } from './components/shared/rich-text-editor/rich-text-editor.component';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';

// Chart.js v4 registration
import {
    Chart,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    TimeSeriesScale,
    BarController,
    LineController,
    DoughnutController,
    Filler
} from 'chart.js';
import 'chartjs-adapter-moment';

// Register Chart.js components globally
Chart.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,         // ✅ For doughnut/pie charts
    Title,
    Tooltip,
    Legend,
    TimeSeriesScale,
    BarController,      // ✅ Register Bar Controller
    LineController,     // ✅ Register Line Controller
    DoughnutController, // ✅ Register Doughnut Controller
    Filler             // ✅ Required for dataset fill property to work
);
// Note: Global Chart.js font defaults are set in graphing.service.ts

export function appFactory() {
    return (): Promise<any> => {
        // reset tracking on app init
        if (TrackingService.read()) {
            TrackingService.clear();
        }
        return new Promise(((resolve, reject) => resolve(true)));
    };
}

export function appConfigFactory(
    stripeService: StripeService,
    runtimeConfig: RuntimeConfigService,
) {
    return (): Promise<void> => {
        // Use native fetch() to bypass Angular's HTTP interceptor chain.
        // Interceptors add withCredentials + Authorization header which triggers
        // a CORS preflight — if the endpoint doesn't exist yet (e.g., E2E running
        // master core), the preflight 404 produces a browser console error.
        return fetch(`${environment.LegFiCoreApi}/api/config`)
            .then(response => response.ok ? response.json() : null)
            .then((data: AppConfigResponse | null) => {
                runtimeConfig.set(data);
                if (data?.paymentsPublicKey) {
                    stripeService.changeKey(data.paymentsPublicKey, {
                        betas: ['blocked_card_brands_beta_2'],
                    });
                }
            })
            .catch(() => {
                // Silent catch — /api/config may fail in environments without the
                // backend endpoint (e.g., E2E). Stripe + reCAPTCHA degrade gracefully.
            });
    };
}

@NgModule({
    declarations: [
        AppComponent,
    ],
    imports: [
        // SharedModule,
        // AppLayoutModulePayhoa,
        BrowserModule,
        BrowserAnimationsModule,
        DatedBrowserComponent,
        // EditorModule,
        RichTextEditorComponent,
        // FormsModule,
        HttpClientModule,
        HttpClientXsrfModule,
        NgbModule,
        MatSnackBarModule,
        // AdminLayoutModule,
        // V2LayoutModule,
        // AppLayoutModule,
        // AuthLayoutModule,
        DragulaModule.forRoot(),
        // ErrorModule,
        SlimLoaderComponent,
        GableDrawerHostComponent,
        ReportBugDrawerHostComponent,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        FileUploadModule,
        // TagsComponent,
        NgxStripeModule.forRoot(undefined, {
            betas: ['blocked_card_brands_beta_2'],
        }),
        GoogleTagManagerModule.forRoot({
            id: environment.GoogleGtm.container,
        }),

        // Always import app routing last!
        AppRoutingModule,
    ],
    providers: [
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SiteIdInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MaintenanceModeInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ContentTypeInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RefreshTokenInterceptor,
            multi: true,
        },
        {
            // Registered LAST on purpose: Angular runs response/error handlers in
            // reverse registration order, so this fires FIRST on the way back and
            // observes every error before another interceptor can swallow it.
            provide: HTTP_INTERCEPTORS,
            useClass: CaptureHttpInterceptor,
            multi: true,
        },
        {
            provide: ApplicationHttpClient,
            useFactory: applicationHttpClientFactory,
            deps: [
                HttpClient,
                Location,
                Router,
            ],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: appFactory,
            deps: [],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: appConfigFactory,
            deps: [StripeService, RuntimeConfigService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (service: IncidentBannerService) => () => service.initialize(),
            deps: [IncidentBannerService],
            multi: true,
        },
        {
            // Start the always-on breadcrumb capture at app boot so the buffer
            // is already filling by the time a super user files a bug report.
            provide: APP_INITIALIZER,
            useFactory: (service: BreadcrumbBufferService) => () => service.start(),
            deps: [BreadcrumbBufferService],
            multi: true,
        },
        LoginGuard,
        ForwardToFormsGuard,
        PermissionsGuard,
        RedirectGuard,
        importProvidersFrom(MatMomentDateModule),
        importProvidersFrom(MatSelectModule),
        importProvidersFrom(MatDatepickerModule),
        importProvidersFrom(MatLegacyDialogModule),
        importProvidersFrom(RecaptchaV3Module),
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            // Resolved at DI time AFTER APP_INITIALIZER (appConfigFactory) has run,
            // so RuntimeConfigService is populated from /api/config before ng-recaptcha
            // ever reads the key. SD-6823 — keep the real site key out of source control.
            useFactory: (runtimeConfig: RuntimeConfigService) => runtimeConfig.getRecaptchaV3SiteKey() ?? '',
            deps: [RuntimeConfigService],
        },
        provideAnimations(),
        provideEnvironmentNgxMask(),
        importProvidersFrom(GalleryModule),
    ],
    bootstrap: [AppComponent],
})
export class AppModule
{
}
